<script setup lang="ts">
import { computed, onMounted, provide, ref, useSlots, watch, type VNode } from "vue";
import { useRoute, useRouter } from "vue-router";

import ColorBadge from "@/components/ui/ColorBadge.vue";
import TabPanelContent from "@/components/ui/TabPanelContent.vue";
import { slugify } from "@/services/utils";

const { usesAnchor = true } = defineProps<{ usesAnchor?: boolean }>();
const emit = defineEmits<{
  tabChange: [tabName: string];
}>();

const slots = useSlots();
const selectedTabSlug = ref("");
const router = useRouter();
const route = useRoute();
provide("selectedTabSlug", selectedTabSlug);
defineExpose({ selectTab });

const tabs = computed(() => {
  const children = slots.default?.({}) || [];
  return children
    .filter((child: VNode) => child.type === TabPanelContent)
    .map((child: VNode) => ({
      name: child.props?.name,
      icon: child.props?.icon,
      disabled: child.props?.disabled,
      badge: child.props?.badge,
      badgeLabel: child.props?.badgeLabel,
      slug: slugify(child.props?.name),
    }));
});

function selectTab(slug: string) {
  const s = slugify(slug);
  emit("tabChange", s);
  selectedTabSlug.value = s;
  if (usesAnchor) {
    const path = `${route.path}#${s}`;
    router.push(path);
  }
}

function selectTabByRoute() {
  const firstTab = tabs.value[0];
  if (firstTab) {
    const selected = route.hash && usesAnchor ? route.hash.replace("#", "") : firstTab.slug || "";
    selectTab(selected);
  }
}

watch(
  () => route,
  () => {
    selectTabByRoute();
  }
);

onMounted(() => {
  selectTabByRoute();
});
</script>

<template>
  <div class="tab-panel">
    <div class="tab-selector-container">
      <div v-if="slots.prefix" class="prefix">
        <slot name="prefix" />
      </div>
      <div class="tab-selector">
        <div
          v-for="({ name, icon, disabled, badge, badgeLabel, slug }, i) in tabs"
          :key="i"
          @click="!disabled && selectTab(slug)"
          class="tab-selector-element"
          :class="{ selected: slug === selectedTabSlug, disabled }"
        >
          <i v-if="icon" class="icon" :class="icon" />

          <span class="name">
            {{ name }}
          </span>

          <ColorBadge
            v-if="badgeLabel"
            :color="badge.color"
            :left-icon="badge.leftIcon"
            :right-icon="badge.rightIcon"
            size="s"
          >
            {{ badgeLabel }}
          </ColorBadge>
        </div>
      </div>
    </div>

    <div class="tabs">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.tab-panel {
  .tab-selector-container {
    display: flex;
    flex-direction: row;

    .prefix {
      display: inline-block;
      padding: var(--numbers-4) 0;
      border-bottom: solid var(--stroke-width-md) var(--border-tertiary);
    }
  }

  .tab-selector {
    display: flex;
    flex: 1;
    flex-flow: row wrap;
    align-items: center;
    border-bottom: solid var(--stroke-width-md) var(--border-tertiary);
    gap: var(--numbers-6);

    .tab-selector-element {
      padding: 0 var(--numbers-4) var(--numbers-6) var(--numbers-4);
      border-bottom: solid var(--stroke-width-lg) transparent;
      color: var(--content-primary);
      cursor: pointer;
      transition:
        border-bottom var(--animation-duration) var(--animation-easing),
        color var(--animation-duration) var(--animation-easing);

      .icon {
        padding-right: var(--numbers-4);
        vertical-align: middle;
      }

      .name {
        display: inline-block;
        padding: var(--numbers-4) 0;
      }

      .color-badge {
        margin-left: var(--numbers-4);
        contain-intrinsic-height: none;
      }

      &.selected {
        border-bottom: solid var(--stroke-width-lg) var(--border-brand);
        color: var(--content-brand);
      }

      &.disabled {
        color: var(--content-secondary);
        cursor: not-allowed;
      }
    }
  }
}
</style>
